import { useEffect, useState } from "react";
import { joinWaitlist, getWaitlistStats } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowRight,
    CheckCircle2,
    Building2,
    Truck,
    Recycle,
    Shield,
    Clock,
    Zap,
} from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import { trackEvent } from "@/lib/analytics";

const INITIAL = {
    full_name: "",
    email: "",
    phone: "",
    city: "Delhi NCR",
    organisation: "",
    waste_type: "",
    waste_volume: "",
    vehicle_type: "",
    areas_covered: "",
    facility_capacity: "",
    material_needed: "",
    message: "",
};

export default function Waitlist() {
    const { t } = useT();
    const [role, setRole] = useState("generator");
    const [form, setForm] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        generator: 0,
        collector: 0,
        processor: 0,
    });

    useEffect(() => {
        let mounted = true;
        getWaitlistStats()
            .then((s) => mounted && setStats(s))
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [success]);

    const ROLES = [
        {
            key: "generator",
            label: t("waitlist.role.generator"),
            Icon: Building2,
            blurb: t("waitlist.role.generator.blurb"),
        },
        {
            key: "collector",
            label: t("waitlist.role.collector"),
            Icon: Truck,
            blurb: t("waitlist.role.collector.blurb"),
        },
        {
            key: "processor",
            label: t("waitlist.role.processor"),
            Icon: Recycle,
            blurb: t("waitlist.role.processor.blurb"),
        },
    ];

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const switchRole = (key) => {
        setRole(key);
        setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.phone) {
            toast.error(t("waitlist.error.required"));
            return;
        }
        setSubmitting(true);
        try {
            const payload = { role, ...form };
            await joinWaitlist(payload);
            trackEvent("waitlist_submit", {
                role,
                city: form.city,
                method: "site_form",
            });
            setSuccess(true);
            toast.success(t("waitlist.success.toast"));
            setForm(INITIAL);
            // Take the user to the success card so they see the confirmation immediately
            setTimeout(() => {
                document
                    .querySelector('[data-testid="waitlist-success"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 80);
        } catch (err) {
            // Prefer the backend's own message; surface network/timeout issues clearly
            let detail = err?.response?.data?.detail;
            if (!detail) {
                if (err?.code === "ECONNABORTED")
                    detail = t("waitlist.error.timeout");
                else if (err?.message === "Network Error" || !err?.response)
                    detail = t("waitlist.error.network");
                else detail = t("waitlist.error.generic");
            }
            toast.error(detail);
            // Log full error to console so it's diagnosable on production
            // eslint-disable-next-line no-console
            console.error("[waitlist] submit failed:", err);
            trackEvent("waitlist_submit_error", {
                role,
                status: err?.response?.status || 0,
                code: err?.code || "unknown",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const ActiveRole = ROLES.find((r) => r.key === role);

    return (
        <section
            id="waitlist"
            data-testid="waitlist-section"
            aria-labelledby="waitlist-headline"
            className="relative py-24 md:py-40 bg-secondary"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-12 md:gap-16">
                    <div className="md:col-span-5">
                        <div
                            data-testid="waitlist-badge"
                            className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-accent/15 border border-accent/30 text-accent text-xs uppercase tracking-[0.22em] font-semibold"
                        >
                            <span className="relative flex h-2 w-2" aria-hidden="true">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                            </span>
                            {t("waitlist.badge")}
                        </div>
                        <h2
                            id="waitlist-headline"
                            className="font-display text-5xl md:text-7xl text-primary tracking-tight leading-[0.95] mb-6"
                        >
                            {t("waitlist.headline.prefix")}{" "}
                            <em className="text-accent">{t("waitlist.headline.accent")}</em>
                        </h2>
                        <p className="text-lg text-foreground/75 leading-relaxed mb-6 max-w-md">
                            {t("waitlist.intro")}
                        </p>

                        {stats.total > 0 && (
                            <div
                                data-testid="waitlist-count"
                                className="inline-flex items-baseline gap-2 mb-8 text-sm text-foreground/70"
                            >
                                <span className="font-display text-primary text-xl font-medium">
                                    {stats.total}
                                </span>
                                <span>
                                    {stats.total === 1
                                        ? t("waitlist.count.one")
                                        : t("waitlist.count.many")}
                                </span>
                            </div>
                        )}

                        <ul className="space-y-3 mb-8 list-none p-0">
                            {[
                                { Icon: Zap, t: t("waitlist.benefits.1") },
                                { Icon: CheckCircle2, t: t("waitlist.benefits.2") },
                                { Icon: Shield, t: t("waitlist.benefits.3") },
                                { Icon: Clock, t: t("waitlist.benefits.4") },
                            ].map(({ Icon, t: text }) => (
                                <li
                                    key={text}
                                    className="flex items-center gap-3 text-foreground/80"
                                >
                                    <Icon size={16} className="text-accent shrink-0" aria-hidden="true" />
                                    <span className="text-sm">{text}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="hidden md:block pt-6 border-t border-primary/15">
                            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
                                {t("waitlist.choose")}
                            </div>
                            <div className="space-y-2">
                                {ROLES.map((r) => (
                                    <button
                                        type="button"
                                        key={r.key}
                                        onClick={() => switchRole(r.key)}
                                        data-testid={`role-pick-${r.key}`}
                                        aria-pressed={role === r.key}
                                        className={`w-full text-left px-4 py-3 rounded-sm border transition-all flex items-center gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                                            role === r.key
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-background border-primary/15 hover:border-primary/40"
                                        }`}
                                    >
                                        <r.Icon size={18} strokeWidth={1.5} aria-hidden="true" />
                                        <div className="flex-1">
                                            <div className="font-medium">{r.label}</div>
                                            <div
                                                className={`text-xs leading-snug ${
                                                    role === r.key
                                                        ? "text-primary-foreground/75"
                                                        : "text-muted-foreground"
                                                }`}
                                            >
                                                {r.blurb}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-7">
                        <div
                            role="tablist"
                            aria-label={t("waitlist.choose")}
                            className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-1"
                        >
                            {ROLES.map((r) => (
                                <button
                                    type="button"
                                    role="tab"
                                    key={r.key}
                                    onClick={() => switchRole(r.key)}
                                    data-testid={`role-tab-${r.key}`}
                                    aria-selected={role === r.key}
                                    className={`px-4 py-2 text-sm rounded-full border whitespace-nowrap transition-all ${
                                        role === r.key
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "border-primary/20 text-foreground/70"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>

                        {success ? (
                            <SuccessCard
                                role={ActiveRole}
                                t={t}
                                onAddAnother={() => setSuccess(false)}
                            />
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                data-testid="waitlist-form"
                                noValidate
                                className="bg-background p-8 md:p-12 rounded-sm border border-primary/10 shadow-[0_30px_60px_-30px_rgba(30,54,40,0.25)]"
                            >
                                <div className="flex items-center gap-3 mb-8">
                                    <ActiveRole.Icon
                                        size={20}
                                        className="text-accent"
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                    <div className="font-display text-2xl text-primary">
                                        {t("waitlist.joiningAs")} {ActiveRole.label}
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                                    <Field label={t("waitlist.field.fullName")} required>
                                        <input
                                            type="text"
                                            data-testid="input-full-name"
                                            className="paper-input"
                                            value={form.full_name}
                                            onChange={update("full_name")}
                                            placeholder={t("waitlist.field.fullName.ph")}
                                            required
                                            autoComplete="name"
                                        />
                                    </Field>
                                    <Field label={t("waitlist.field.email")} required>
                                        <input
                                            type="email"
                                            data-testid="input-email"
                                            className="paper-input"
                                            value={form.email}
                                            onChange={update("email")}
                                            placeholder={t("waitlist.field.email.ph")}
                                            required
                                            autoComplete="email"
                                            inputMode="email"
                                        />
                                    </Field>
                                    <Field label={t("waitlist.field.phone")} required>
                                        <input
                                            type="tel"
                                            data-testid="input-phone"
                                            className="paper-input"
                                            value={form.phone}
                                            onChange={update("phone")}
                                            placeholder={t("waitlist.field.phone.ph")}
                                            required
                                            autoComplete="tel"
                                            inputMode="tel"
                                        />
                                    </Field>
                                    <Field label={t("waitlist.field.city")}>
                                        <input
                                            type="text"
                                            data-testid="input-city"
                                            className="paper-input"
                                            value={form.city}
                                            onChange={update("city")}
                                            placeholder="Delhi NCR"
                                            autoComplete="address-level2"
                                        />
                                    </Field>

                                    {role === "generator" && (
                                        <>
                                            <Field label={t("waitlist.field.organisation")}>
                                                <input
                                                    type="text"
                                                    data-testid="input-organisation"
                                                    className="paper-input"
                                                    value={form.organisation}
                                                    onChange={update("organisation")}
                                                    placeholder={t("waitlist.field.organisation.ph")}
                                                    autoComplete="organization"
                                                />
                                            </Field>
                                            <Field label={t("waitlist.field.wasteType")}>
                                                <input
                                                    type="text"
                                                    data-testid="input-waste-type"
                                                    className="paper-input"
                                                    value={form.waste_type}
                                                    onChange={update("waste_type")}
                                                    placeholder={t("waitlist.field.wasteType.ph")}
                                                />
                                            </Field>
                                            <Field label={t("waitlist.field.wasteVolume")} full>
                                                <input
                                                    type="text"
                                                    data-testid="input-waste-volume"
                                                    className="paper-input"
                                                    value={form.waste_volume}
                                                    onChange={update("waste_volume")}
                                                    placeholder={t("waitlist.field.wasteVolume.ph")}
                                                />
                                            </Field>
                                        </>
                                    )}

                                    {role === "collector" && (
                                        <>
                                            <Field label={t("waitlist.field.vehicle")}>
                                                <input
                                                    type="text"
                                                    data-testid="input-vehicle-type"
                                                    className="paper-input"
                                                    value={form.vehicle_type}
                                                    onChange={update("vehicle_type")}
                                                    placeholder={t("waitlist.field.vehicle.ph")}
                                                />
                                            </Field>
                                            <Field label={t("waitlist.field.areas")}>
                                                <input
                                                    type="text"
                                                    data-testid="input-areas-covered"
                                                    className="paper-input"
                                                    value={form.areas_covered}
                                                    onChange={update("areas_covered")}
                                                    placeholder={t("waitlist.field.areas.ph")}
                                                />
                                            </Field>
                                        </>
                                    )}

                                    {role === "processor" && (
                                        <>
                                            <Field label={t("waitlist.field.capacity")}>
                                                <input
                                                    type="text"
                                                    data-testid="input-facility-capacity"
                                                    className="paper-input"
                                                    value={form.facility_capacity}
                                                    onChange={update("facility_capacity")}
                                                    placeholder={t("waitlist.field.capacity.ph")}
                                                />
                                            </Field>
                                            <Field label={t("waitlist.field.material")}>
                                                <input
                                                    type="text"
                                                    data-testid="input-material-needed"
                                                    className="paper-input"
                                                    value={form.material_needed}
                                                    onChange={update("material_needed")}
                                                    placeholder={t("waitlist.field.material.ph")}
                                                />
                                            </Field>
                                        </>
                                    )}

                                    <Field label={t("waitlist.field.message")} full>
                                        <textarea
                                            data-testid="input-message"
                                            className="paper-input resize-none"
                                            rows={3}
                                            value={form.message}
                                            onChange={update("message")}
                                            placeholder={t("waitlist.field.message.ph")}
                                        />
                                    </Field>
                                </div>

                                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
                                        {t("waitlist.disclaimer")}
                                    </p>
                                    <button
                                        type="submit"
                                        data-testid="waitlist-submit"
                                        disabled={submitting}
                                        className="relative inline-flex items-center gap-3 bg-primary text-primary-foreground pl-7 pr-6 py-4 rounded-full text-sm tracking-wide cta-glow shadow-[0_18px_40px_-12px_rgba(30,54,40,0.55)] hover:shadow-[0_24px_60px_-12px_rgba(30,54,40,0.75)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent group"
                                    >
                                        {submitting
                                            ? t("waitlist.submit.loading")
                                            : t("waitlist.submit")}
                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-foreground/15 transition-transform duration-300 group-hover:translate-x-1">
                                            <ArrowRight size={14} />
                                        </span>
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children, required, full }) {
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`}>
            <span className="block text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
                {label}
                {required && (
                    <span className="text-accent ml-1" aria-hidden="true">
                        *
                    </span>
                )}
            </span>
            {children}
        </label>
    );
}

function SuccessCard({ role, t, onAddAnother }) {
    return (
        <div
            data-testid="waitlist-success"
            role="status"
            aria-live="polite"
            className="bg-background p-10 md:p-14 rounded-sm border-2 border-accent/40 text-center shadow-[0_30px_60px_-30px_rgba(30,54,40,0.35)] relative overflow-hidden"
        >
            {/* Subtle confetti dots */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
                <span className="absolute top-6 left-8 w-2 h-2 rounded-full bg-accent/70" />
                <span className="absolute top-10 right-10 w-1.5 h-1.5 rounded-full bg-primary/60" />
                <span className="absolute bottom-8 left-14 w-1.5 h-1.5 rounded-full bg-primary/40" />
                <span className="absolute bottom-12 right-16 w-2 h-2 rounded-full bg-accent/60" />
                <span className="absolute top-1/2 left-4 w-1 h-1 rounded-full bg-accent/80" />
                <span className="absolute top-1/3 right-6 w-1 h-1 rounded-full bg-primary/50" />
            </div>

            <div
                className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/15 mb-6 ring-4 ring-accent/10"
                aria-hidden="true"
            >
                <CheckCircle2 size={34} className="text-accent" strokeWidth={1.5} />
            </div>

            <div className="relative text-xs uppercase tracking-[0.3em] text-accent font-semibold mb-4">
                {t("waitlist.success.eyebrow")}
            </div>

            <h3
                data-testid="waitlist-success-title"
                className="font-display text-5xl md:text-6xl text-primary tracking-tight mb-5 leading-[0.95]"
            >
                {t("waitlist.success.title")}
            </h3>

            <p className="relative text-lg text-foreground/80 max-w-md mx-auto leading-relaxed mb-6">
                {t("waitlist.success.body.prefix")} <strong className="text-primary">{role.label}</strong>{t("waitlist.success.body.suffix")}
            </p>

            <p className="relative text-sm text-muted-foreground max-w-sm mx-auto mb-8">
                {t("waitlist.success.nextSteps")}
            </p>

            <button
                type="button"
                onClick={onAddAnother}
                data-testid="waitlist-add-another"
                className="relative inline-flex items-center gap-2 text-sm text-primary border-b border-primary/40 hover:border-primary pb-0.5"
            >
                {t("waitlist.success.again")}
            </button>
        </div>
    );
}
